import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { inAppWallet, preAuthenticate } from 'thirdweb/wallets/in-app';
import { useSetActiveWallet } from 'thirdweb/react';
import { polygonAmoy } from 'thirdweb/chains';
import { useActiveAccount } from 'thirdweb/react';
import { createWallet } from 'thirdweb/wallets';

import { client } from '../../config/thirdwebConfig';
import { AuthLogoSection } from '../../components/Hero';
import { AuthForm } from '../../components/Form';
import { AuthButtons } from '../../components/Buttons';
import { OtpInput } from '../../components/OtpInput';

import useFantomStarterAdminApi from '../../hooks/useFantomStarterAdminApi';
import useToken from '../../hooks/useToken';
import useModal from '../../hooks/useModal';
import { loader } from '../../assets';

// Error types for better error handling
type AuthErrorType = 
  | 'connection_error' 
  | 'wallet_error' 
  | 'api_error' 
  | 'signature_error' 
  | 'timeout_error' 
  | 'unknown_error';

const Signup = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const wallet = inAppWallet();
  const setActiveAccount = useSetActiveWallet();
  const activeAccount = useActiveAccount();
  const location = useLocation();
  const currentPath = location.pathname;

  const {
    getUser,
    registerUser,
    admin: { login },
  } = useFantomStarterAdminApi();
  const { setToken } = useToken();

  const now = Date.now();

  const [isUserFetched, setIsUserFetched] = useState<boolean | null>(null);
  const [isAlreadyUser, setIsAlreadyUser] = useState<boolean>(false);
  const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false);
  const [address, setAddress] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isSigningTx, setIsSigningTx] = useState<boolean>(false);
  const [isRegisteringUser, setIsRegisteringUser] = useState<boolean>(false);
  
  // Authentication feedback states
  const [authStatus, setAuthStatus] = useState<string>(''); // For status messages
  const [authError, setAuthError] = useState<{type: AuthErrorType | null, message: string | null}>({
    type: null,
    message: null
  });

  const openOtpInputModal = useCallback((email: string) => {
    openModal(
      <OtpInput
        headerTitle="Sign Up"
        email={email}
        onClose={() => {
          setAuthStatus('');
          closeModal();
        }}
        onVerify={async (otpValue, setIsVerifyingOtp) => {
          try {
            setIsVerifyingOtp(true);
            setAuthStatus('Verifying OTP code...');
            
            await wallet.connect({
              client,
              chain: polygonAmoy,
              strategy: 'email',
              email,
              verificationCode: otpValue,
            });
            setAuthStatus('OTP verified. Creating your account...');
            await setActiveAccount(wallet);
            closeModal();
          } catch (error) {
            console.error('Error while verifying OTP:', error);
            setAuthError({
              type: 'wallet_error',
              message: 'Failed to verify OTP. Please try again.'
            });
          } finally {
            setIsVerifyingOtp(false);
          }
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = useCallback(async (email: string) => {
    try {
      // Clear any previous errors
      setAuthError({ type: null, message: null });
      setAuthStatus('Sending verification code...');
      setIsSendingOtp(true);
      
      await preAuthenticate({
        client,
        strategy: 'email',
        email,
      });
      
      setAuthStatus('Code sent successfully!');
      setIsSendingOtp(false);
      openOtpInputModal(email);
    } catch (error) {
      console.error('Error while Pre authenticating:', error);
      setIsSendingOtp(false);
      setAuthError({
        type: 'connection_error',
        message: 'Failed to send verification code. Please try again.'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueWithGoogle = useCallback(async () => {
    try {
      // Clear any previous errors
      setAuthError({ type: null, message: null });
      setAuthStatus('Connecting with Google...');
      
      await wallet.connect({
        client,
        chain: polygonAmoy,
        strategy: 'google',
      });
      
      setAuthStatus('Google account connected. Creating your account...');
      await setActiveAccount(wallet);
    } catch (error) {
      console.error('Error while connecting with Google:', error);
      setAuthError({
        type: 'connection_error', 
        message: 'Failed to connect with Google. Please try again.'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueWithApple = useCallback(async () => {
    try {
      // Clear any previous errors
      setAuthError({ type: null, message: null });
      setAuthStatus('Connecting with Apple...');
      
      await wallet.connect({
        client,
        chain: polygonAmoy,
        strategy: 'google', // Note: this appears to be using Google strategy for Apple
      });
      
      setAuthStatus('Apple account connected. Creating your account...');
      await setActiveAccount(wallet);
    } catch (error) {
      console.error('Error while continuing with Apple:', error);
      setAuthError({
        type: 'connection_error',
        message: 'Failed to connect with Apple. Please try again.'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueWithMetamask = useCallback(async () => {
    try {
      // Clear any previous errors
      setAuthError({ type: null, message: null });
      setAuthStatus('Connecting with MetaMask...');
      
      const metamask = createWallet('io.metamask');
      await wallet.connect({
        strategy: 'wallet',
        chain: polygonAmoy,
        wallet: metamask,
        client,
      });
      
      setAuthStatus('MetaMask connected. Creating your account...');
      await setActiveAccount(wallet);
    } catch (error) {
      console.error('Error while continuing with MetaMask:', error);
      setAuthError({
        type: 'wallet_error',
        message: 'Failed to connect with MetaMask. Please make sure MetaMask is installed and unlocked.'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignLoginTx = async () => {
    if (!address) {
      console.error('Wallet address not found!');
      setAuthError({
        type: 'wallet_error',
        message: 'Wallet address not found. Please try again.'
      });
      return;
    }
    if (!activeAccount) {
      console.error('Active account not found!');
      setAuthError({
        type: 'wallet_error',
        message: 'Active account not found. Please try again.'
      });
      return;
    }
    
    setIsSigningTx(true);
    setAuthStatus('Signing authentication message...');
    
    try {
      // Show user feedback during signing process
      setAuthStatus('Please confirm the signature request in your wallet...');
      
      const signature = await activeAccount.signMessage({
        message: `FantomStarter-Admin-Login-${address}-${now.toString()}`,
      });
      
      setAuthStatus('Signature confirmed. Logging in...');
      
      const result = await login(address, signature, now);
      setToken(result?.data?.token);
      
      setAuthStatus('Login successful! Redirecting...');
      navigate('/');
    } catch (error) {
      console.error('Error while signing login transaction:', error);
      setAuthError({
        type: 'signature_error',
        message: 'Failed to sign login message. Please try again.'
      });
    } finally {
      setIsSigningTx(false);
    }
  };

  const handleRegisterUser = async () => {
    if (!address) {
      console.error('Wallet address not found!');
      setAuthError({
        type: 'wallet_error',
        message: 'Wallet address not found. Please try again.'
      });
      return;
    }
    
    setIsRegisteringUser(true);
    setAuthStatus('Registering your account...');
    
    try {
      const result = await registerUser(address);
      if (result?.data?.status === 'created') {
        setAuthStatus('Registration successful! Proceeding to login...');
        setIsAlreadyUser(true);
      }
    } catch (error) {
      console.error('Error while registering user:', error);
      setAuthError({
        type: 'api_error',
        message: 'Failed to register user. Please try again.'
      });
    } finally {
      setIsRegisteringUser(false);
    }
  };

  useEffect(() => {
    if (address) {
      let isMounted = true;
      setAuthStatus('Checking if user exists...');
      
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), 5000)
      );

      const fetchUserPromise = getUser(address);

      Promise.race([fetchUserPromise, timeoutPromise])
        .then((result) => {
          if (isMounted) {
            setIsUserFetched(true);
            if (result?.data?.id) {
              setAuthStatus('Account already exists. Preparing to sign in...');
              setIsAlreadyUser(true);
            }
          }
        })
        .catch((error) => {
          if (isMounted) {
            if (error.message === 'Request timed out') {
              console.log('Request timed out');
              setAuthError({
                type: 'timeout_error',
                message: 'Connection timed out. Please try again.'
              });
            } else {
              console.log('Error fetching user:', error);
              setAuthError({
                type: 'api_error',
                message: 'Failed to verify user. Please try again.'
              });
            }
            setIsUserFetched(true);
          }
        });

      return () => {
        isMounted = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    if (activeAccount && activeAccount.address) {
      setAddress(activeAccount.address);
      setIsConnected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccount]);

  useEffect(() => {
    if (isUserFetched && !isAlreadyUser) {
      console.log('User not found, registering...');
      handleRegisterUser();
    }
    if (isUserFetched && isAlreadyUser) {
      console.log('User already exists, signing in...');
      handleSignLoginTx();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserFetched, isAlreadyUser]);

  return (
    <main className="bg-white w-screen min-h-screen sm:h-screen flex flex-col sm:flex-row items-center">
      <AuthLogoSection />
      {!isConnected && (
        <section className="w-full sm:w-2/5 px-4 py-6 sm:p-6 flex flex-col items-center">
          <div className="flex flex-col items-center mb-2 sm:mb-0">
            <h1 className="text-2xl sm:text-3xl font-semibold">Sign Up</h1>
            <p className="text-base sm:text-lg font-normal mt-2 text-center px-3">
              Enter your email below to create your account
            </p>
          </div>
          
          {/* Show error message if present */}
          {authError.message && (
            <div className="w-full max-w-[360px] mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md animate-fadeIn">
              {authError.message}
            </div>
          )}
          
          <AuthForm
            onSubmit={handleFormSubmit}
            hideTnCBox
            isSubmitting={isSendingOtp}
          />
          <section className="flex gap-2 justify-center items-center w-full max-w-[360px] mx-auto mt-6 sm:mt-8 leading-none text-center text-[#334155]">
            <div className="flex-1 shrink self-stretch my-auto h-px border border-solid basis-0 border-[#E2E8F0]" />
            <div className="self-stretch my-auto text-xs sm:text-sm font-medium">
              OR CONTINUE WITH
            </div>
            <div className="flex-1 shrink self-stretch my-auto h-px border border-solid basis-0 border-[#E2E8F0]" />
          </section>
          <AuthButtons
            onContinueWithGoogle={handleContinueWithGoogle}
            onContinueWithApple={handleContinueWithApple}
            onContinueWithMetamask={handleContinueWithMetamask}
          />
          <section className="flex flex-row items-center gap-1 sm:gap-2 justify-center mt-4 sm:mt-5">
            <p className="text-xs sm:text-sm text-[#334155]">
              Already have an account?
            </p>
            <button
              className="btn-text px-0 font-medium text-xs sm:text-sm text-[#6741c6]"
              onClick={() => navigate('/auth/login')}
            >
              Log in
            </button>
          </section>
        </section>
      )}
      {isConnected && address && activeAccount && (
        <section className="w-full sm:w-2/5 px-4 py-6 sm:p-6 flex flex-col items-center">
          <div className="flex flex-col items-center">
            <h1 className="text-2xl sm:text-3xl font-semibold">Sign up</h1>
            <p className="text-base sm:text-lg text-center font-normal mt-2">
              {authStatus || "Please wait while we sign you in"}
            </p>
          </div>
          
          {/* Show spinner with status message */}
          <div className="mt-6 sm:mt-8 flex flex-col items-center">
            <img
              src={loader}
              alt="loader"
              className="w-8 h-8 sm:w-10 sm:h-10 animate-spin"
            />
            {isSigningTx && (
              <p className="mt-4 text-sm text-[#6741c6] animate-pulse">
                Please check your wallet for signature request
              </p>
            )}
          </div>
          
          {/* Show error message if present */}
          {authError.message && (
            <div className="w-full max-w-[360px] mt-6 sm:mt-8 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md animate-fadeIn">
              <div className="flex items-center">
                <span className="text-xl mr-2">⚠️</span>
                <span>{authError.message}</span>
              </div>
              <button 
                className="w-full mt-3 btn-text border border-red-200 text-red-700 hover:bg-red-100"
                onClick={() => {
                  setAuthError({ type: null, message: null });
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </section>
      )}
    </main>
  );
};

export default Signup;